import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Table, Button, Modal, Form, Input, Tag, Space, Typography, message, Radio, Descriptions, Row, Col, Dropdown, DatePicker, Select } from 'antd';
import { DownOutlined, RollbackOutlined, CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined, DeleteOutlined, SettingOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import leadReturnsService from '../../../services/leadReturns';
import leadsService from '../../../services/leads';

// Add styles for private rows
const styles = `
  .row-private {
    background-color: #fff1f0 !important;
  }
  .row-private:hover > td {
    background-color: #ffccc7 !important;
  }
`;

// Add the styles to the document
const styleSheet = document.createElement("style");
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

const ReturnsPage = () => {
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [selectedReturn, setSelectedReturn] = React.useState(null);
  const [isModalVisible, setIsModalVisible] = React.useState(false);
  const [isLeadModalVisible, setIsLeadModalVisible] = React.useState(false);
  const [selectedData, setSelectedData] = React.useState(null);
  const [expandedRowKeys, setExpandedRowKeys] = React.useState([]);
  const [dateRange, setDateRange] = React.useState(null);
  const [statusFilter, setStatusFilter] = React.useState(null);
  const [selectedStatus, setSelectedStatus] = React.useState(null);
  const [showTemplates, setShowTemplates] = React.useState(false);

  // Add premade messages
  const premadeMessages = {
    approved: [
      {
        label: "Standard Approval",
        message: "We have reviewed your return request and are happy to inform you that it has been approved. The refund will be processed within 5-7 business days. Thank you for your patience."
      },
      {
        label: "Quick Approval",
        message: "Your return request has been approved. The refund process has been initiated and you should receive your refund shortly. Thank you for your business."
      },
      {
        label: "Approval with Apology",
        message: "We apologize for any inconvenience caused. Your return request has been approved and we will process your refund immediately. We value your business and hope to serve you better in the future."
      },
      {
        label: "Approval with Next Steps",
        message: "Your return request has been approved. We will process your refund within 5-7 business days. You will receive an email confirmation once the refund is processed. Please allow 2-3 business days for the refund to appear in your account after processing."
      },
      {
        label: "Approval with Survey Request",
        message: "Your return request has been approved and we will process your refund shortly. We value your feedback and would appreciate if you could take a moment to complete our customer satisfaction survey. This will help us improve our services for future customers."
      },
      {
        label: "Approval with Alternative Product",
        message: "We have approved your return request and will process your refund. We noticed you might be interested in our premium leads package, which offers better quality leads and a higher success rate. Would you like to learn more about this alternative?"
      }
    ],
    rejected: [
      {
        label: "Standard Rejection",
        message: "After careful review of your return request, we regret to inform you that we cannot approve this return as it does not meet our return policy criteria. Please refer to our return policy for more information."
      },
      {
        label: "Rejection with Explanation",
        message: "We have reviewed your return request and unfortunately cannot approve it at this time. The request falls outside our standard return window of 30 days from purchase. We appreciate your understanding."
      },
      {
        label: "Rejection with Policy Reference",
        message: "We regret to inform you that your return request cannot be approved. According to our return policy, leads must be returned within 30 days of purchase and must be in unused condition. Your request does not meet these criteria."
      },
      {
        label: "Rejection with Usage Explanation",
        message: "We cannot approve your return request as the lead has already been contacted and processed. Our policy states that leads cannot be returned once they have been contacted, as this would affect the lead's value and our business operations."
      },
      {
        label: "Rejection with Quality Assurance",
        message: "We have reviewed your return request and cannot approve it at this time. Our quality assurance team has verified that the lead information provided was accurate and complete at the time of purchase. We stand behind the quality of our leads and would be happy to discuss any specific concerns you may have."
      },
      {
        label: "Rejection with Support Offer",
        message: "We cannot approve your return request, but we would like to offer you additional support in working with this lead. Our team can provide guidance on lead engagement strategies and best practices. Would you like to schedule a consultation with our lead success team?"
      }
    ]
  };

  // Add function to handle template selection
  const handleTemplateSelect = (template) => {
    form.setFieldsValue({
      admin_response: template.message
    });
  };

  // Add function to handle status change
  const handleStatusChange = (e) => {
    setSelectedStatus(e.target.value);
    form.setFieldsValue({ admin_response: '' }); // Clear the response when status changes
  };

  const { data: returns, isLoading } = useQuery({
    queryKey: ['returns', dateRange, statusFilter],
    queryFn: () => leadReturnsService.getReturns({
      startDate: dateRange?.[0]?.format('YYYY-MM-DD'),
      endDate: dateRange?.[1]?.format('YYYY-MM-DD'),
      status: statusFilter
    })
  });

  const updateMutation = useMutation({
    mutationFn: (data) => leadReturnsService.updateReturn(selectedReturn.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['returns']);
      message.success('Return request processed successfully');
      setIsModalVisible(false);
      setSelectedReturn(null);
      setSelectedStatus(null);
      setShowTemplates(false);
      form.resetFields();
    },
    onError: (error) => {
      message.error(error.response?.data?.message || 'Failed to process return request');
    }
  });

  const deleteLeadMutation = useMutation({
    mutationFn: (leadId) => leadsService.deleteLead(leadId),
    onSuccess: () => {
      message.success('Lead deleted successfully');
      queryClient.invalidateQueries(['returns']);
    },
    onError: (error) => {
      message.error(error.response?.data?.message || 'Failed to delete lead');
    }
  });

  const togglePublicMutation = useMutation({
    mutationFn: (leadId) => leadsService.togglePublicStatus(leadId),
    onSuccess: () => {
      message.success('Lead public status updated successfully');
      queryClient.invalidateQueries(['returns']);
    },
    onError: (error) => {
      message.error(error.response?.data?.message || 'Failed to update lead public status');
    }
  });

  const handleProcess = (record) => {
    setSelectedReturn(record);
    setIsModalVisible(true);
  };

  const handleViewLead = (record) => {
    setSelectedData({
      lead: record.lead_purchase?.lead,
      purchase: record.lead_purchase
    });
    setIsLeadModalVisible(true);
  };

  const handleDeleteLead = (leadId) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this lead?',
      content: 'This action cannot be undone. All associated data will be permanently removed.',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: () => deleteLeadMutation.mutate(leadId)
    });
  };

  const handleTogglePublic = (leadId, currentStatus) => {
    Modal.confirm({
      title: `Are you sure you want to make this lead ${currentStatus ? 'private' : 'public'}?`,
      content: currentStatus 
        ? 'Making this lead private will hide it from normal users.'
        : 'Making this lead public will make it visible to normal users.',
      okText: 'Yes',
      okType: 'primary',
      cancelText: 'No',
      onOk: () => togglePublicMutation.mutate(leadId)
    });
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      updateMutation.mutate(values);
    } catch (error) {
      // Form validation failed
    }
  };

  const handleExpandClick = (record) => {
    const key = record.id;
    setExpandedRowKeys(expandedRowKeys.includes(key) 
      ? expandedRowKeys.filter(k => k !== key)
      : [...expandedRowKeys, key]
    );
  };

  const calculateStats = (data) => {
    if (!data) return { total: 0, pending: 0, approved: 0, rejected: 0 };
    
    return {
      total: data.length,
      pending: data.filter(return_ => return_.status === 'pending').length,
      approved: data.filter(return_ => return_.status === 'approved').length,
      rejected: data.filter(return_ => return_.status === 'rejected').length
    };
  };

  const prepareChartData = () => {
    if (!returns?.data) return [];
    
    // Group returns by date
    const groupedData = returns.data.reduce((acc, return_) => {
      const date = new Date(return_.created_at).toLocaleDateString();
      if (!acc[date]) {
        acc[date] = {
          date,
          total: 0,
          pending: 0,
          approved: 0,
          rejected: 0
        };
      }
      acc[date].total += 1;
      acc[date][return_.status] += 1;
      return acc;
    }, {});

    return Object.values(groupedData).sort((a, b) => 
      new Date(a.date) - new Date(b.date)
    );
  };

  const preparePieData = () => {
    const stats = calculateStats(returns?.data);
    return [
      { name: 'Pending', value: stats.pending },
      { name: 'Approved', value: stats.approved },
      { name: 'Rejected', value: stats.rejected }
    ];
  };

  const COLORS = ['#faad14', '#52c41a', '#ff4d4f'];

  const stats = calculateStats(returns?.data);
  const chartData = prepareChartData();
  const pieData = preparePieData();

  const columns = [
    {
      title: 'Name',
      key: 'user',
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <div>{record.user?.name} {record.user?.last_name || ''}</div>
        </Space>
      ),
    },
    {
      title: 'Email',
      key: 'email',
      width: 150,
      render: (_, record) => record.user?.email || 'N/A'
    },
    {
      title: 'Phone',
      key: 'phone',
      width: 150,
      render: (_, record) => record.user?.phone || 'N/A'
    },
    {
      title: 'Purchase Details',
      key: 'purchase',
      render: (_, record) => {
        const purchase = record.lead_purchase;
        if (!purchase) return 'N/A';
        return (
          <Space>
            <span>Transaction ID: {purchase.transaction_id}</span>
          </Space>
        );
      },
    },
    {
      title: 'Return Details',
      key: 'return',
      render: (_, record) => (
        <Button 
          type="link" 
          onClick={() => handleExpandClick(record)}
          icon={<DownOutlined rotate={expandedRowKeys.includes(record.id) ? 180 : 0} />}
        >
          <Tag color={
            record.status === 'pending' ? 'warning' :
            record.status === 'approved' ? 'success' : 'error'
          }>
            {record.status.toUpperCase()}
          </Tag>
        </Button>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      width: 100,
      render: (_, record) => {
        const menuItems = [
          {
            key: 'view',
            label: 'View Lead Details',
            onClick: () => handleViewLead(record)
          }
        ];

        // Only show process option for pending returns
        if (record.status === 'pending') {
          menuItems.push({
            key: 'process',
            label: 'Process Return',
            onClick: () => handleProcess(record)
          });
        }

        // Only show delete and toggle public options if there's a lead
        if (record.lead_purchase?.lead?.id) {
          const isPublic = record.lead_purchase.lead.is_public == true;
          menuItems.push(
            {
              key: 'toggle-public',
              label: isPublic ? 'Make Private' : 'Make Public',
              icon: isPublic ? <EyeInvisibleOutlined /> : <EyeOutlined />,
              onClick: () => handleTogglePublic(record.lead_purchase.lead.id, isPublic)
            },
            {
              key: 'delete',
              label: 'Delete Lead',
              danger: true,
              icon: <DeleteOutlined />,
              onClick: () => handleDeleteLead(record.lead_purchase.lead.id)
            }
          );
        }

        return (
          <Dropdown
            menu={{
              items: menuItems
            }}
            trigger={['click']}
          >
            <Button type="text" icon={<SettingOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  const expandedRowRender = (record) => {
    return (
      <div style={{ padding: '16px' }}>
        <Table
          columns={[
            {
              title: 'Field',
              dataIndex: 'field',
              key: 'field',
              width: '30%',
            },
            {
              title: 'Value',
              dataIndex: 'value',
              key: 'value',
              width: '70%',
              render: (value, record) => {
                if (record.isHeader) {
                  return <Typography.Text strong>{value}</Typography.Text>;
                }
                if (record.field === 'Status') {
                  return (
                    <Tag color={
                      value === 'pending' ? 'warning' :
                      value === 'approved' ? 'success' : 'error'
                    }>
                      {value.toUpperCase()}
                    </Tag>
                  );
                }
                if (record.field === 'Requested At' || record.field === 'Processed At') {
                  return new Date(value).toLocaleString();
                }
                return value;
              }
            }
          ]}
          dataSource={[
            { field: 'Return Request Details', value: '', isHeader: true },
            { field: 'Return Reason', value: record.return_reason },
            { field: 'Status', value: record.status },
            { field: 'Requested At', value: record.created_at },
            { field: 'Processed At', value: record.processed_at || 'Not processed yet' },
            { field: 'Admin Response', value: record.admin_response || 'No response yet' },
            { field: 'Purchase Information', value: '', isHeader: true },
            { field: 'Transaction ID', value: record.lead_purchase?.transaction_id || 'N/A' },
            { field: 'Amount', value: record.lead_purchase?.amount ? `$${record.lead_purchase.amount}` : 'N/A' },
            { field: 'Payment Status', value: record.lead_purchase?.payment_status ? 'Paid' : 'Unpaid' },
            { field: 'Purchase Date', value: record.lead_purchase?.created_at ? new Date(record.lead_purchase.created_at).toLocaleString() : 'N/A' }
          ]}
          pagination={false}
          rowKey={(record) => record.field}
          onRow={(record) => ({
            style: record.isHeader ? { 
              background: '#f5f5f5',
              fontWeight: 'bold'
            } : {}
          })}
          showHeader={false}
          size="small"
          style={{ background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
        />
      </div>
    );
  };

  return (
    <div className="page-container" style={{
      background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%)',
      minHeight: '100vh',
      padding: '24px'
    }}>
      <div className="page-header" style={{
        marginBottom: '32px',
        padding: '24px',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '300px',
          height: '300px',
          background: 'radial-gradient(circle at top right, rgba(24,144,255,0.1) 0%, rgba(24,144,255,0) 70%)',
          pointerEvents: 'none'
        }} />
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          position: 'relative',
          zIndex: 1
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(24,144,255,0.25)',
            transition: 'transform 0.3s ease'
          }}>
            <RollbackOutlined style={{ fontSize: '20px', color: '#fff' }} />
          </div>
          <div>
            <Typography.Title level={2} style={{
              margin: 0,
              color: '#1f1f1f',
              background: 'linear-gradient(135deg, #1f1f1f 0%, #434343 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontSize: '24px'
            }}>
              Return Requests
            </Typography.Title>
            <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
              View and manage lead return requests
            </Typography.Text>
          </div>
        </div>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={16}>
          <Card style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
          }}>
            <div style={{ 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '16px'
            }}>
              <div style={{ 
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <RollbackOutlined style={{ fontSize: '20px', color: '#fff' }} />
                </div>
                <Typography.Title level={5} style={{ margin: 0 }}>Return Trends</Typography.Title>
              </div>
              <Space>
                <Select
                  style={{ width: 150 }}
                  placeholder="Filter by Status"
                  allowClear
                  value={statusFilter}
                  onChange={setStatusFilter}
                  options={[
                    { value: 'pending', label: 'Pending', color: '#faad14' },
                    { value: 'approved', label: 'Approved', color: '#52c41a' },
                    { value: 'rejected', label: 'Rejected', color: '#ff4d4f' }
                  ]}
                  optionRender={(option) => (
                    <Tag color={option.data.color}>
                      {option.data.label}
                    </Tag>
                  )}
                />
                <DatePicker.RangePicker
                  value={dateRange}
                  onChange={setDateRange}
                  style={{ width: 300 }}
                  allowClear
                  placeholder={['Start Date', 'End Date']}
                />
              </Space>
            </div>
            <div style={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="total" 
                    stroke="#1890ff" 
                    name="Total Returns"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="pending" 
                    stroke="#faad14" 
                    name="Pending"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="approved" 
                    stroke="#52c41a" 
                    name="Approved"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="rejected" 
                    stroke="#ff4d4f" 
                    name="Rejected"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
          }}>
            <div style={{ 
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '16px'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <RollbackOutlined style={{ fontSize: '20px', color: '#fff' }} />
              </div>
              <Typography.Title level={5} style={{ margin: 0 }}>Return Status Distribution</Typography.Title>
            </div>
            <div style={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <RollbackOutlined style={{ fontSize: '24px', color: '#fff' }} />
              </div>
              <div>
                <div style={{ 
                  fontSize: '14px', 
                  color: '#666',
                  fontWeight: 500,
                  height: 17
                }}>
                  Total Returns
                </div>
                <div style={{ 
                  fontSize: '24px', 
                  fontWeight: 600,
                  color: '#1890ff',
                  lineHeight: 1.2
                }}>
                  {stats.total}
                </div>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                background: 'linear-gradient(135deg, #faad14 0%, #d48806 100%)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <ClockCircleOutlined style={{ fontSize: '24px', color: '#fff' }} />
              </div>
              <div>
                <div style={{ 
                  fontSize: '14px', 
                  color: '#666',
                  fontWeight: 500,
                  height: 17
                }}>
                  Pending
                </div>
                <div style={{ 
                  fontSize: '24px', 
                  fontWeight: 600,
                  color: '#faad14',
                  lineHeight: 1.2
                }}>
                  {stats.pending}
                </div>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <CheckCircleOutlined style={{ fontSize: '24px', color: '#fff' }} />
              </div>
              <div>
                <div style={{ 
                  fontSize: '14px', 
                  color: '#666',
                  fontWeight: 500,
                  height: 17
                }}>
                  Approved
                </div>
                <div style={{ 
                  fontSize: '24px', 
                  fontWeight: 600,
                  color: '#52c41a',
                  lineHeight: 1.2
                }}>
                  {stats.approved}
                </div>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                background: 'linear-gradient(135deg, #ff4d4f 0%, #cf1322 100%)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <CloseCircleOutlined style={{ fontSize: '24px', color: '#fff' }} />
              </div>
              <div>
                <div style={{ 
                  fontSize: '14px', 
                  color: '#666',
                  fontWeight: 500,
                  height: 17
                }}>
                  Rejected
                </div>
                <div style={{ 
                  fontSize: '24px', 
                  fontWeight: 600,
                  color: '#ff4d4f',
                  lineHeight: 1.2
                }}>
                  {stats.rejected}
                </div>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      <Card className="shadow-sm" style={{ 
        borderRadius: '12px', 
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
      }}>
        <Table
          size="small"
          columns={columns}
          dataSource={returns?.data}
          rowKey="id"
          loading={isLoading}
          scroll={{ x: 1200 }}
          expandable={{
            expandedRowRender,
            expandedRowKeys,
            expandRowByClick: false,
            expandIcon: () => null
          }}
          pagination={{
            total: returns?.total,
            pageSize: returns?.per_page,
            current: returns?.current_page,
            showSizeChanger: true,
            showQuickJumper: true,
          }}
          rowClassName={(record) => record.lead_purchase?.lead?.is_public == false ? 'row-private' : ''}
        />
      </Card>

      {/* Process Return Modal */}
      <Modal
        title="Process Return Request Decision"
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setSelectedReturn(null);
          setSelectedStatus(null);
          setShowTemplates(false);
          form.resetFields();
        }}
        footer={[
          <div key="footer-content" style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <Button
                type="primary"
                loading={updateMutation.isLoading}
                onClick={handleSubmit}
              >
                Submit
              </Button>
              {selectedStatus && (
                <Button 
                  type="link" 
                  onClick={() => setShowTemplates(!showTemplates)}
                  icon={showTemplates ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                >
                  {showTemplates ? 'Hide Templates' : 'Show Templates'}
                </Button>
              )}
            </div>
            {selectedStatus && showTemplates && (
              <div style={{ 
                borderTop: '1px solid #f0f0f0', 
                paddingTop: '16px',
                width: '100%'
              }}>
                <Typography.Text strong style={{ display: 'block', marginBottom: '12px' }}>
                  Response Templates
                </Typography.Text>
                <Space direction="vertical" style={{ width: '100%' }}>
                  {premadeMessages[selectedStatus].map((template, index) => (
                    <Button
                      key={`${selectedStatus}-${index}`}
                      type="default"
                      onClick={() => handleTemplateSelect(template)}
                      style={{ width: '100%', textAlign: 'left', marginBottom: '8px' }}
                    >
                      {template.label}
                    </Button>
                  ))}
                </Space>
              </div>
            )}
          </div>
        ]}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
        >
          <Form.Item
            name="status"
            rules={[{ required: true, message: 'Please select a decision' }]}
          >
            <Radio.Group onChange={handleStatusChange}>
              <Radio value="approved">Approve</Radio>
              <Radio value="rejected">Reject</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            name="admin_response"
            label="Response"
            rules={[
              { required: true, message: 'Please provide a response' },
              { min: 10, message: 'Response must be at least 10 characters long' }
            ]}
          >
            <Input.TextArea
              rows={4}
              placeholder="Please provide a response to the return request..."
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Lead Information Modal */}
      <Modal
        title="Lead and Purchase Information"
        open={isLeadModalVisible}
        onCancel={() => {
          setIsLeadModalVisible(false);
          setSelectedData(null);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setIsLeadModalVisible(false);
            setSelectedData(null);
          }}>
            Close
          </Button>
        ]}
        width={800}
      >
        {selectedData && (
          <>
            <Typography.Title level={4}>Purchase Details</Typography.Title>
            <Descriptions bordered column={2} style={{ marginBottom: 24 }}>
              <Descriptions.Item label="Amount">${selectedData.purchase?.amount}</Descriptions.Item>
              <Descriptions.Item label="Transaction ID">{selectedData.purchase?.transaction_id}</Descriptions.Item>
              <Descriptions.Item label="Payment Method">{selectedData.purchase?.payment_method}</Descriptions.Item>
              <Descriptions.Item label="Payment Status">
                <Tag color={selectedData.purchase?.payment_status ? 'success' : 'error'}>
                  {selectedData.purchase?.payment_status ? 'Paid' : 'Unpaid'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Agreed">
                <Tag color={selectedData.purchase?.is_agreed ? 'success' : 'error'}>
                  {selectedData.purchase?.is_agreed ? 'Yes' : 'No'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Purchase Date">{new Date(selectedData.purchase?.created_at).toLocaleString()}</Descriptions.Item>
            </Descriptions>

            <Typography.Title level={4}>Lead Information</Typography.Title>
            <Descriptions bordered column={2}>
              <Descriptions.Item label="Name">{selectedData.lead?.first_name} {selectedData.lead?.last_name}</Descriptions.Item>
              <Descriptions.Item label="Phone">{selectedData.lead?.phone}</Descriptions.Item>
              <Descriptions.Item label="Email">{selectedData.lead?.email}</Descriptions.Item>
              <Descriptions.Item label="State">
                {selectedData.lead?.state_name || selectedData.lead?.state?.name || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Birth Date">{selectedData.lead?.birth_date}</Descriptions.Item>
              <Descriptions.Item label="Type of Insurance">{selectedData.lead?.type_of_life_insurance}</Descriptions.Item>
              <Descriptions.Item label="Coverage">{selectedData.lead?.final_expense_coverage || selectedData.lead?.coverage_amount || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Beneficiary">{selectedData.lead?.beneficiary}</Descriptions.Item>
              <Descriptions.Item label="Annual Income">{selectedData.lead?.annual_income || 'N/A'}</Descriptions.Item>
              {/* <Descriptions.Item label="Mortgage Amount">{selectedData.lead?.mortgage_amount_owed || 'N/A'}</Descriptions.Item> */}
              <Descriptions.Item label="Lead Price">${selectedData.lead?.lead_price}</Descriptions.Item>
              <Descriptions.Item label="Availability Status">
                <Tag color={selectedData.lead?.availability_status ? 'success' : 'error'}>
                  {selectedData.lead?.availability_status ? 'Available' : 'Unavailable'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Last Activity">{selectedData.lead?.last_activity || 'N/A'}</Descriptions.Item>
              {/* <Descriptions.Item label="Can We Contact">{selectedData.lead?.can_we_contact || 'N/A'}</Descriptions.Item> */}
              <Descriptions.Item label="Similar Plan Contact">{selectedData.lead?.similar_plan_contact_permission ? 'Yes' : 'No'}</Descriptions.Item>
            </Descriptions>
          </>
        )}
      </Modal>
    </div>
  );
};

export default ReturnsPage; 